import { cn } from "../lib/utils.js";
import "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { cva } from "class-variance-authority";
import { CircleNotchIcon } from "@phosphor-icons/react/CircleNotch";
import { Slot, Slottable } from "@radix-ui/react-slot";
//#region src/components/button.tsx
const defaultStyles = cn("tw tw:inline-flex tw:items-center tw:whitespace-nowrap tw:font-medium", "tw:transition-[color,background-color,box-shadow] tw:disabled:opacity-30", "[&_svg]:tw:pointer-events-none [&_svg:not([class*='size-'])]:tw:size-4 tw:shrink-0 [&_svg]:tw:shrink-0", "tw:outline-none tw:focus-visible:border-ring tw:focus-visible:ring-ring/50 tw:focus-visible:ring-[3px]", "tw:aria-invalid:ring-destructive/20 tw:dark:aria-invalid:ring-destructive/40 tw:aria-invalid:border-destructive", "tw:relative", "tw:rounded-md tw:new:rounded-xs");
const defaultVariants = {
	variant: "default",
	size: "default",
	alignContent: "center"
};
const buttonVariantStyles = {
	default: cn("tw:bg-primary tw:text-primary-foreground tw:shadow-xs tw:hover:bg-primary/90 tw:data-[state=open]:bg-primary/90", "tw:disabled:hover:bg-primary", "tw:new:bg-primary-purple tw:new:text-white tw:new:hover:bg-primary-purple/80 tw:new:data-[state=open]:bg-primary-purple/80 tw:new:shadow-none", "tw:new:disabled:hover:bg-primary-purple"),
	destructive: cn("tw:bg-destructive tw:text-white tw:shadow-xs tw:hover:bg-destructive/90 tw:data-[state=open]:bg-destructive/90 tw:focus-visible:ring-destructive/20 tw:dark:focus-visible:ring-destructive/40", "tw:disabled:hover:bg-destructive", "tw:new:bg-secondary-clay tw:new:text-white tw:new:hover:bg-secondary-clay/80 tw:new:data-[state=open]:bg-secondary-clay/80 tw:new:focus-visible:ring-secondary-clay/20 tw:new:dark:focus-visible:ring-secondary-clay/40 tw:new:shadow-none", "tw:new:disabled:hover:bg-secondary-clay"),
	"light-destructive": cn("tw:bg-destructive/10 tw:text-destructive tw:shadow-xs tw:hover:bg-destructive/20 tw:data-[state=open]:bg-destructive/20 tw:focus-visible:ring-destructive/20 tw:dark:focus-visible:ring-destructive/40", "tw:disabled:hover:bg-destructive/10", "tw:new:bg-secondary-clay/10 tw:new:text-secondary-clay tw:new:hover:bg-secondary-clay/20 tw:new:data-[state=open]:bg-secondary-clay/20 tw:new:focus-visible:ring-secondary-clay/20 tw:new:dark:focus-visible:ring-secondary-clay/40 tw:new:shadow-none", "tw:new:disabled:hover:bg-secondary-clay/10"),
	outline: cn("tw:border tw:border-input tw:bg-background tw:shadow-xs tw:hover:bg-accent tw:hover:text-accent-foreground tw:data-[state=open]:bg-accent tw:data-[state=open]:text-accent-foreground", "tw:disabled:hover:bg-background tw:disabled:hover:text-current", "tw:new:border tw:new:border-stroke tw:new:bg-white tw:new:text-midnight tw:new:hover:bg-sidebar tw:new:data-[state=open]:bg-sidebar tw:new:shadow-none", "tw:new:disabled:hover:bg-white tw:new:disabled:hover:text-midnight"),
	secondary: cn("tw:bg-accent tw:text-accent-foreground tw:shadow-xs tw:hover:bg-accent/80 tw:data-[state=open]:bg-accent/80", "tw:disabled:hover:bg-accent", "tw:new:bg-sidebar tw:new:text-midnight tw:new:hover:bg-concrete/50 tw:new:data-[state=open]:bg-concrete/50 tw:new:shadow-none", "tw:new:disabled:hover:bg-sidebar"),
	tertiary: cn("tw:cursor-pointer tw:border-0 tw:bg-transparent tw:font-inherit tw:font-normal tw:text-midnight/70 tw:no-underline tw:shadow-none tw:transition-none", "tw:hover:bg-transparent tw:hover:text-midnight tw:data-[state=open]:bg-transparent tw:data-[state=open]:text-midnight", "tw:focus-visible:ring-2 tw:focus-visible:ring-primary-purple/40", "tw:disabled:hover:bg-transparent tw:disabled:hover:text-midnight/70", "tw:rounded-none tw:new:rounded-none"),
	ghost: cn("tw:hover:bg-accent tw:hover:text-accent-foreground tw:data-[state=open]:bg-accent tw:data-[state=open]:text-accent-foreground", "tw:disabled:hover:bg-transparent tw:disabled:hover:text-current", "tw:new:text-current tw:new:hover:bg-current/10 tw:new:data-[state=open]:bg-current/10", "tw:new:disabled:hover:bg-transparent"),
	link: cn("tw:underline-offset-4 tw:hover:underline tw:data-[state=open]:underline", "tw:text-primary", "tw:new:text-primary-purple", "tw:disabled:hover:no-underline tw:new:disabled:hover:no-underline")
};
/** Runtime list of Button variant names, derived from the variant style map. */
const BUTTON_VARIANTS = Object.keys(buttonVariantStyles);
const buttonVariants = cva(defaultStyles, {
	variants: {
		variant: buttonVariantStyles,
		size: {
			default: "tw:h-8 tw:px-3 tw:has-[svg]:px-2.5 tw:text-sm",
			lg: "tw:h-10 tw:px-6 tw:has-[svg]:px-4 tw:text-md",
			icon: "tw:size-8 tw:text-sm",
			inline: "tw:h-8 tw:p-0 tw:has-[svg]:p-0 tw:text-sm [&_svg:not([class*='size-'])]:tw:size-3.5"
		},
		alignContent: {
			center: "tw:justify-center",
			start: "tw:justify-start"
		}
	},
	defaultVariants
});
const ButtonContent = ({ children, loading, size, alignContent }) => /* @__PURE__ */ jsxs(Fragment$1, { children: [loading && /* @__PURE__ */ jsx("div", {
	className: "tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center",
	children: /* @__PURE__ */ jsx(CircleNotchIcon, {
		className: "tw:animate-spin",
		weight: "bold"
	})
}), /* @__PURE__ */ jsx("div", {
	className: cn("tw:inline-flex tw:items-center tw:gap-2 tw:w-full", alignContent === "start" ? "tw:justify-start" : "tw:justify-center", size === "inline" && "tw:w-auto tw:gap-1", loading ? "tw:invisible" : "tw:visible"),
	children
})] });
const blockInteraction = (event) => {
	event.preventDefault();
	event.stopPropagation();
};
const getDisabledProps = ({ asChild, isDisabled }) => {
	if (!asChild) return { disabled: isDisabled };
	if (!isDisabled) return {};
	return {
		"aria-disabled": true,
		tabIndex: -1,
		onClickCapture: blockInteraction
	};
};
const Button = ({ asChild = false, className, disabled = false, loading = false, size = "default", variant = "default", children, alignContent = "center", ...props }) => {
	const Comp = asChild ? Slot : "button";
	const isDisabled = disabled || loading;
	return /* @__PURE__ */ jsx(Comp, {
		"data-slot": "button",
		className: cn(buttonVariants({
			variant,
			size,
			alignContent
		}), loading && "tw:pointer-events-none", className),
		...props,
		...getDisabledProps({
			asChild,
			isDisabled
		}),
		children: asChild ? /* @__PURE__ */ jsx(Slottable, {
			child: children,
			children: (slottable) => /* @__PURE__ */ jsx(ButtonContent, {
				loading,
				size,
				alignContent,
				children: slottable
			})
		}) : /* @__PURE__ */ jsx(ButtonContent, {
			loading,
			size,
			alignContent,
			children
		})
	});
};
//#endregion
export { BUTTON_VARIANTS, Button, buttonVariants };

//# sourceMappingURL=button.js.map